import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import cn from 'classnames'
import ReactTooltip from 'react-tooltip'
import { format } from 'date-fns'

import { IInventoryEvent, IProduct } from '../../types/products'
import { IAccount, IBranch } from '../../types/user'
import { IPaginationState } from '../../types/common'
import useThunkDispatch from '../../common/useThunkDispatch'
import Select from '../../components/Select'
import {
  requestHistoryLocations,
  requestInventoryEvents,
  IInventoryEventsResult,
} from './inventoryActions'
import BoxIcon from '../../icons/box.svg'
import CartPlusIcon from '../../icons/cart_plus.svg'
import CartMinusIcon from '../../icons/cart_minus.svg'
import TransferArrowsIcon from '../../icons/transfer_arrows.svg'
import EditSquareIcon from '../../icons/edit_square.svg'
import MoveToIcon from '../../icons/move_to.svg'
import MoveOutIcon from '../../icons/move_out.svg'
import SigmaSquareIcon from '../../icons/sigma_square.svg'
import SquareArrowUpRightIcon from '../../icons/square_arrow_up_right.svg'

interface IProductFormProps {
  product: IProduct
  branch: IBranch
  account?: IAccount
  onProductRefresh: (
    patch: Partial<Pick<IProduct, 'inventory' | 'updatedAt'>>,
  ) => void
}

type EventType = IInventoryEvent['eventType']

// one history line: a checkout taken from several bins is one row of its bin records
type Row = IInventoryEvent[]

interface ILocationOption {
  label: string
  value: number | null
}

const ALL_LOCATIONS: ILocationOption = { label: 'All locations', value: null }

// saved on the inventory page's history entry, so Back reopens this view
export interface IHistoryReturnState {
  productId: number
  historyFilters: string[]
  // with its label: the option list is not loaded until the menu opens
  historyLocation: ILocationOption
}

const PER_PAGE = 25
const DETAILS_TOOLTIP = 'history-details'

const EMPTY_PAGINATION_STATE: IPaginationState = {
  endCursor: '',
  startCursor: '',
  hasNextPage: false,
  hasPreviousPage: false,
}

const FILTERS: Array<{ label: string; types: EventType[] }> = [
  { label: 'Shipments', types: ['shipment'] },
  { label: 'Checkouts', types: ['checkout'] },
  { label: 'Adjustments', types: ['manual'] },
  { label: 'Transfers', types: ['transfer_from', 'transfer_to'] },
  { label: 'Moves', types: ['move_out', 'move_in'] },
  { label: 'Counts', types: ['cycle_count'] },
]

const EVENTS: Record<EventType, { title: string; Icon: FC }> = {
  shipment: { title: 'Shipment', Icon: BoxIcon },
  checkout: { title: 'Checkout', Icon: CartMinusIcon },
  manual: { title: 'Manual adjustment', Icon: EditSquareIcon },
  transfer_from: { title: 'Transfer', Icon: TransferArrowsIcon },
  transfer_to: { title: 'Transfer', Icon: TransferArrowsIcon },
  move_out: { title: 'Move Out', Icon: MoveOutIcon },
  move_in: { title: 'Move In', Icon: MoveToIcon },
  cycle_count: { title: 'Count', Icon: SigmaSquareIcon },
}

const isMove = (event: IInventoryEvent) =>
  event.eventType === 'move_out' || event.eventType === 'move_in'

// a checkout event that returns stock is written by reopen
const isReopen = (event: IInventoryEvent) =>
  event.eventType === 'checkout' && event.amount > 0

const titleOf = (event: IInventoryEvent) =>
  isReopen(event) ? 'Checkout reopen' : EVENTS[event.eventType].title

// a shipment's popup names nobody
const BY_LABELS: Record<EventType, string | undefined> = {
  shipment: undefined,
  checkout: 'Checked out by',
  manual: 'Updated by',
  transfer_from: 'Transferred by',
  transfer_to: 'Transferred by',
  move_out: 'By',
  move_in: 'By',
  cycle_count: 'By',
}

// a checkout fills the technician's truck, whose code is hidden while trucks are off
const moveEnds = (event: IInventoryEvent) => {
  const extra = event.additional
  const ends = [
    extra.fromLocationCode,
    extra.toLocationCode || extra.toBranchName,
  ]
    .filter(Boolean)
    .join(' to ')
  if (ends || !extra.checkoutName) return ends
  return event.eventType === 'move_in' ? 'to truck' : 'from truck'
}

// the bin records of one checkout item are written together, so they are neighbours
const groupRows = (events: IInventoryEvent[]): Row[] => {
  const rows: Row[] = []
  for (const event of events) {
    const row = rows[rows.length - 1]
    const itemId = event.additional.checkoutItemId
    if (
      row &&
      itemId &&
      row[0].additional.checkoutItemId === itemId &&
      Math.sign(row[0].amount) === Math.sign(event.amount)
    ) {
      row.push(event)
    } else {
      rows.push([event])
    }
  }
  return rows
}

const amountOf = (row: Row) => row.reduce((sum, e) => sum + e.amount, 0)

// a location record's stockValue is that bin or truck's stock, any other record's is the branch total
const stockRows = (event: IInventoryEvent): Array<[string, number]> => {
  const extra = event.additional
  if (isMove(event)) {
    const from = extra.fromLocationCode || 'source'
    const to = extra.toLocationCode || extra.toBranchName || 'destination'
    const rows: Array<[string, number | undefined]> = [
      [`Left in ${from}`, extra.fromStockValue],
      [`Now in ${to}`, extra.toStockValue],
    ]
    return rows.filter((row): row is [string, number] => row[1] !== undefined)
  }
  if (!event.locationId) return [['On hand', event.stockValue]]
  const where = extra.locationCode ? `in ${extra.locationCode}` : 'in bin'
  return [[`On hand ${where}`, event.stockValue]]
}

// whose stock a row shows: the selected location's, otherwise the branch total
const rowStock = (event: IInventoryEvent, locationId: number | null) =>
  locationId ? event.stockValue : event.branchStockValue

const signed = (amount: number) =>
  amount > 0 ? `+${amount.toLocaleString()}` : amount.toLocaleString()

const InventoryEvents: FC<IProductFormProps> = ({
  product,
  branch,
  account,
  onProductRefresh,
}) => {
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [inventoryEvents, setInventoryEvents] = useState<IInventoryEvent[]>([])
  const [paginationState, setPaginationState] = useState<IPaginationState>(
    EMPTY_PAGINATION_STATE,
  )
  const location = useLocation()
  const navigate = useNavigate()
  const returnState = location.state as IHistoryReturnState | null
  const returning = returnState?.productId === product.id
  const [filters, setFilters] = useState<string[]>(
    returning ? returnState.historyFilters : [],
  )
  const [historyLocation, setHistoryLocation] = useState<ILocationOption>(
    returning ? returnState.historyLocation : ALL_LOCATIONS,
  )
  const locationId = historyLocation.value
  // null until the menu is first opened
  const [locationOptions, setLocationOptions] = useState<
    ILocationOption[] | null
  >(null)
  const [locationsLoading, setLocationsLoading] = useState(false)
  // a related row outside the selected location: jump to it once the unfiltered list loads
  const [pendingJumpId, setPendingJumpId] = useState<number | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  // a response is applied only if no newer request started meanwhile
  const requestRef = useRef(0)

  const dispatch = useThunkDispatch()
  const branchId = branch && branch.id
  const eventTypes = FILTERS.filter((f) => filters.includes(f.label)).flatMap(
    (f) => f.types,
  )

  const [highlightId, setHighlightId] = useState<number | null>(null)
  const rows = useMemo(() => groupRows(inventoryEvents), [inventoryEvents])

  const fetchEvents = (after?: string, throughEventId: number | null = null) =>
    dispatch<Promise<IInventoryEventsResult>>(
      requestInventoryEvents({
        productId: product.id,
        branchId: branch.id,
        accountId: account && account.id,
        eventTypes,
        locationId,
        throughEventId,
        pagination: throughEventId ? { after } : { first: PER_PAGE, after },
      }),
    )

  const loadInventoryEvents = async () => {
    const request = ++requestRef.current
    setLoading(true)
    const result = await fetchEvents()
    if (request !== requestRef.current) return
    setInventoryEvents(result?.events || [])
    setPaginationState(result?.paginationState || EMPTY_PAGINATION_STATE)
    onProductRefresh({
      inventory: result?.inventory,
      updatedAt: result?.updatedAt,
    })
    setLoading(false)
  }

  const loadMoreEvents = async (throughEventId: number | null = null) => {
    const request = requestRef.current
    setLoadingMore(true)
    const result = await fetchEvents(paginationState.endCursor, throughEventId)
    setLoadingMore(false)
    if (request !== requestRef.current) return false
    const events = result?.events || []
    // the target is outside the current filters
    if (throughEventId && !events.some((e) => e.id === throughEventId))
      return false
    setInventoryEvents((loaded) => [...loaded, ...events])
    const state = result?.paginationState || EMPTY_PAGINATION_STATE
    // a through-load stops at its target, so its hasNextPage says nothing about older rows
    setPaginationState(throughEventId ? { ...state, hasNextPage: true } : state)
    return true
  }

  // older related rows may not be loaded yet: fetch everything down to them in one request
  const jumpTo = async (id: number) => {
    // a page load in flight would append the same rows twice
    if (loadingMore) return
    ReactTooltip.hide()
    const loaded = inventoryEvents.some((e) => e.id === id)
    if (loaded || (await loadMoreEvents(id))) {
      setHighlightId(id)
    } else if (locationId) {
      setPendingJumpId(id)
      setHistoryLocation(ALL_LOCATIONS)
    }
  }

  useEffect(() => {
    if (branchId) {
      loadInventoryEvents()
    }
  }, [product.id, branchId, filters, locationId])

  const loadLocationOptions = async () => {
    if (locationOptions || locationsLoading) return
    setLocationsLoading(true)
    const locations = await dispatch(
      requestHistoryLocations({
        productId: product.id,
        branchId: branch.id,
        accountId: account && account.id,
      }),
    ).finally(() => setLocationsLoading(false))
    setLocationOptions([
      ALL_LOCATIONS,
      ...locations.map((l) => ({ label: l.code, value: l.id })),
    ])
  }

  useEffect(() => {
    if (loading || !pendingJumpId) return
    setPendingJumpId(null)
    jumpTo(pendingJumpId)
  }, [loading])

  // fetch the next page once the sentinel below the list scrolls into view
  useEffect(() => {
    if (loading || loadingMore || !paginationState.hasNextPage) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMoreEvents()
    })
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [loading, loadingMore, paginationState])

  useEffect(() => {
    ReactTooltip.rebuild()
  }, [rows])

  const rowOf = (id: number) => rows.find((row) => row.some((e) => e.id === id))

  useEffect(() => {
    if (!highlightId) return
    const row = rowOf(highlightId)
    if (row)
      document
        .getElementById(`history-event-${row[0].id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const timer = setTimeout(() => setHighlightId(null), 2000)
    return () => clearTimeout(timer)
  }, [highlightId])

  const toggleFilter = (label: string) =>
    setFilters((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    )

  const inboundPath = (id: number) =>
    account
      ? `/clients/${account.id}/branches/${branch.id}/inbound_inventories/${id}`
      : `/inbound_inventories/${id}`

  const renderMeta = (row: Row) => {
    const event = row[0]
    const extra = event.additional
    const by = event.createdByUser?.name

    switch (event.eventType) {
      case 'shipment':
        return extra.orderNumber ? (
          <Link
            className="history-row_link"
            to={inboundPath(extra.inboundInventoryId)}
            state={{ orderNumber: extra.orderNumber, fromHistory: true }}
            onClick={() =>
              navigate(location, {
                replace: true,
                state: {
                  productId: product.id,
                  historyFilters: filters,
                  historyLocation,
                },
              })
            }
          >
            <SquareArrowUpRightIcon />
            Order #{extra.orderNumber}
          </Link>
        ) : (
          by && `by ${by}`
        )
      case 'checkout': {
        const bins = row.map((e) => e.additional.locationCode).filter(Boolean)
        const where = extra.truckCode
          ? `to ${extra.truckCode}`
          : bins.length > 0 &&
            `${event.amount < 0 ? 'from' : 'to'} ${bins.join(', ')}`
        return (
          <>
            {extra.relatedEventId ? (
              <button
                type="button"
                className="history-row_link clear-button"
                disabled={loadingMore}
                onClick={() => jumpTo(extra.relatedEventId)}
              >
                <SquareArrowUpRightIcon />
                {extra.checkoutName}
              </button>
            ) : (
              extra.checkoutName
            )}{' '}
            {[
              where,
              !isReopen(event) &&
                extra.consumerName &&
                `(${extra.consumerName})`,
              by && `by ${by}`,
            ]
              .filter(Boolean)
              .join(' ')}
          </>
        )
      }
      case 'transfer_to':
        return `from ${extra.branchName}`
      case 'transfer_from':
        return `to ${extra.branchName}`
      case 'move_out':
      case 'move_in':
        return [moveEnds(event), by && `by ${by}`].filter(Boolean).join(' ')
      case 'manual':
        return [extra.locationCode, by && `by ${by}`].filter(Boolean).join(', ')
      default:
        return [extra.locationCode, by && `by ${by}`].filter(Boolean).join(' ')
    }
  }

  const renderDetails = (row: Row) => {
    const event = row[0]
    const extra = event.additional
    const checkout = event.eventType === 'checkout'
    const info: Array<[string | undefined, string | undefined]> = [
      ['Date', format(event.eventCreatedAt, 'MMM D, YYYY h:mma')],
      [BY_LABELS[event.eventType], event.createdByUser?.name],
      ['Checked out to', extra.consumerName],
      [
        event.eventType === 'transfer_to' ? 'Source branch' : 'Target branch',
        extra.branchName,
      ],
      ['From', extra.fromLocationCode],
      ['To', extra.toLocationCode || extra.toBranchName],
      ['Transfer', extra.id],
      ['Reason', extra.reason],
    ]
    // a bin checkout lists each bin it took stock from
    const sections: Array<{ bin?: string; figures: Array<[string, string]> }> =
      checkout && extra.locationCode
        ? row.map((e) => ({
            bin: e.additional.locationCode,
            figures: [
              ['Amount', signed(e.amount)],
              ['On hand', e.stockValue.toLocaleString()],
            ],
          }))
        : [
            {
              figures: [
                ['Amount', signed(event.amount)],
                ...stockRows(event).map(([label, value]): [string, string] => [
                  label,
                  value.toLocaleString(),
                ]),
              ],
            },
          ]

    return (
      <div className="history-details">
        {info
          .filter(([label, value]) => label && value)
          .map(([label, value]) => (
            <div key={label} className="history-details_row">
              {label}
              <strong>{value}</strong>
            </div>
          ))}
        {sections.map(({ bin, figures }, index) => (
          <React.Fragment key={index}>
            <div className="history-details_divider" />
            {bin && <div className="history-details_row">{bin}</div>}
            {figures.map(([label, value]) => (
              <div key={label} className="history-details_row">
                {label}
                <strong>{value}</strong>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className="product-inventory">
      <div className="history-location">
        <Select<number | null>
          value={historyLocation}
          options={locationOptions ?? [ALL_LOCATIONS]}
          isLoading={locationsLoading}
          onMenuOpen={loadLocationOptions}
          onChange={(option) => option && setHistoryLocation(option)}
        />
      </div>
      <div className="history-chips">
        <button
          type="button"
          className={cn('history-chip clear-button', {
            '-active': !filters.length,
          })}
          onClick={() => setFilters([])}
        >
          All
        </button>
        {FILTERS.map(({ label }) => (
          <button
            key={label}
            type="button"
            className={cn('history-chip clear-button', {
              '-active': filters.includes(label),
            })}
            onClick={() => toggleFilter(label)}
          >
            {label}
          </button>
        ))}
      </div>
      {loading && <div className="list_placeholder">Loading history...</div>}
      {!loading && !inventoryEvents.length && (
        <div className="list_placeholder">No history yet</div>
      )}
      {!loading && (
        <div className="history-list">
          {rows.map((row) => {
            const event = row[0]
            const amount = amountOf(row)
            const reopen = isReopen(event)
            const Icon = reopen ? CartPlusIcon : EVENTS[event.eventType].Icon
            const stock = rowStock(event, locationId)

            return (
              <div
                key={event.id}
                id={`history-event-${event.id}`}
                className={cn('history-row', {
                  '-highlighted': row.some((e) => e.id === highlightId),
                })}
                data-tip={event.id}
                data-for={DETAILS_TOOLTIP}
              >
                <div
                  className={cn('history-row_icon', { '-mirrored': reopen })}
                >
                  <Icon />
                </div>
                <div className="history-row_info">
                  <div className="history-row_line">
                    <span className="history-row_text">
                      {titleOf(event)} · {format(event.eventCreatedAt, 'MMM D')}
                    </span>
                    <span
                      className={cn('history-row_delta', {
                        '-positive': amount > 0,
                        '-negative': amount < 0,
                      })}
                    >
                      {signed(amount)}
                    </span>
                  </div>
                  <div className="history-row_line -meta">
                    <span className="history-row_text">{renderMeta(row)}</span>
                    {stock !== null && <span>{stock.toLocaleString()}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <ReactTooltip
        id={DETAILS_TOOLTIP}
        className="history-tooltip"
        place="bottom"
        effect="solid"
        event="click"
        globalEventOff="click"
        // without capture react-tooltip stops the click before React handlers in the row
        isCapture
        getContent={(id) => {
          const row = rows.find((r) => String(r[0].id) === id)
          return row && renderDetails(row)
        }}
      />
      {!loading && paginationState.hasNextPage && (
        <div ref={sentinelRef} className="list_placeholder">
          {loadingMore && 'Loading more...'}
        </div>
      )}
    </div>
  )
}

export default memo(InventoryEvents)
